/**
 * Per-word prefix search and ranking used by the operations command palette.
 *
 * The original filter did a single `.includes()` of the whole query against the
 * display name, so typing multiple words in a different order (or with a
 * trailing partial word) matched nothing: e.g. "searc o" would not find
 * "Search Orgs". Matching now works per word, and `scoreMatch` ranks the hits
 * so the closest ones sort to the top.
 */

/**
 * Explicit, independent contributions to a match score. A total score is just
 * the sum of these parts, so a reader can see exactly where it came from — no
 * magic numbers buried in the logic.
 */
export const SCORE_WEIGHTS = {
  /** A query token that equals a whole word, e.g. "orgs" in "Search Orgs". */
  EXACT_WORD_MATCH: 10,
  /** A query token that only prefixes a word, e.g. "sea" in "Search". */
  PREFIX_MATCH: 5,
  /**
   * A query token that prefixes no word but is within a small edit distance of
   * one, e.g. the typo "serch" for "search". Scored below a clean prefix hit so
   * exact > prefix > fuzzy always holds.
   */
  FUZZY_MATCH: 2,
  /** The whole query appears as one contiguous substring of the text. */
  CONTIGUOUS_QUERY_BONUS: 3,
  /** Scaled (0..1) by how early in the text a token's word appears. */
  EARLY_POSITION_BONUS: 2,
} as const;

/**
 * Split text into lowercased word tokens, breaking on whitespace and common
 * separators (`_ - . /` etc.) so identifiers like `upgrade_latest` are matched
 * per word.
 */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Levenshtein edit distance between two strings, using the classic two-row DP
 * (only the previous and current rows are kept). Used for typo tolerance; no
 * external dependency.
 */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Ranks how well `text` matches `query`. Returns 0 when it does not match, so
 * the score doubles as the filter (keep entries where `scoreMatch > 0`).
 *
 * Scoring is order-independent and case-insensitive, summed from
 * {@link SCORE_WEIGHTS}:
 *  1. Each query token scores an exact whole-word match highest, a prefix-only
 *     match next, and a bounded-typo (fuzzy) match lowest; if a token matches
 *     no word even fuzzily, the whole score is 0.
 *  2. Each exact/prefix token adds a bonus scaled to how early its word appears.
 *  3. A one-off bonus when the raw query appears as a contiguous substring.
 *
 * @example
 * scoreMatch("Search Orgs", "searc o") // > 0
 * scoreMatch("Search Orgs", "serch orgs") // > 0 (typo tolerated)
 * scoreMatch("Cache Warm", "cache") > scoreMatch("Warm Cache", "cache") // earlier wins
 */
export function scoreMatch(text: string, query: string): number {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return 0;

  const words = tokenize(text);
  if (words.length === 0) return 0;

  let score = 0;

  // 1 + 2. Per-token word match, plus an early-position bonus for each token.
  for (const token of queryTokens) {
    // Prefer an exact whole-word match; otherwise take the earliest prefix.
    const exactIndex = words.indexOf(token);
    const prefixIndex =
      exactIndex !== -1
        ? exactIndex
        : words.findIndex((word) => word.startsWith(token));

    if (prefixIndex !== -1) {
      score +=
        exactIndex !== -1
          ? SCORE_WEIGHTS.EXACT_WORD_MATCH
          : SCORE_WEIGHTS.PREFIX_MATCH;

      // Earlier words rank higher: 1 for the first word, approaching 0 later.
      const earliness = (words.length - prefixIndex) / words.length;
      score += earliness * SCORE_WEIGHTS.EARLY_POSITION_BONUS;
      continue;
    }

    // Typo tolerance: no prefix hit, so fall back to a bounded edit distance.
    // Allow ~1 edit for short tokens and ~2 for longer ones, but never more
    // than token.length - 1, so a 1-2 char token can't fuzzily match anything.
    const maxEdits = Math.min(token.length - 1, token.length <= 4 ? 1 : 2);
    const isFuzzy =
      maxEdits >= 1 &&
      words.some((word) => editDistance(token, word) <= maxEdits);

    // A token that matches no word even fuzzily disqualifies the whole text.
    if (!isFuzzy) return 0;
    score += SCORE_WEIGHTS.FUZZY_MATCH;
  }

  // 3. Reward the query appearing verbatim as one contiguous run in the text.
  const needle = query.trim().toLowerCase();
  if (needle && text.toLowerCase().includes(needle)) {
    score += SCORE_WEIGHTS.CONTIGUOUS_QUERY_BONUS;
  }

  return score;
}

/** Rank items whose text matches the query, best first; ties keep input order. */
export function rankMatches<T>(
  items: T[],
  query: string,
  textOf: (item: T) => string,
): T[] {
  return items
    .map((item) => ({ item, score: scoreMatch(textOf(item), query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);
}

/**
 * Returns true when every token in `query` is a prefix of at least one word in
 * `text`. Case-insensitive; an empty query matches everything.
 *
 * @example
 * matchesSearch("Search Orgs", "searc o") // true
 * matchesSearch("Upgrade Latest", "up la") // true
 * matchesSearch("Restart", "reindex")      // false
 */
export function matchesSearch(text: string, query: string): boolean {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return true;
  const words = tokenize(text);
  return queryTokens.every((token) =>
    words.some((word) => word.startsWith(token)),
  );
}
